import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityToolbar } from './components/AccessibilityToolbar';
import { PWAInstallBanner } from './components/PWAInstallBanner';
import { SuperAdminDashboard } from './components/SuperAdminDashboard';
import { TenantAdminDashboard } from './components/TenantAdminDashboard';
import { StudentPortalDashboard } from './components/StudentPortalDashboard';
import { DownloadAppsPage } from './components/DownloadAppsPage';
import { AACBoard } from './components/AACBoard';
import { ExecutiveDashboard } from './components/ExecutiveDashboard';
import { SaaSLandingPage } from './components/SaaSLandingPage';
import { AuthModal } from './components/AuthModal';
import { ModuleExplorer } from './components/ModuleExplorer';
import { MobileAppNavigation } from './components/MobileAppNavigation';
import { audioEngine } from './utils/audioEngine';
import { 
  Shield, Volume2, TrendingUp, LogIn, LogOut, User, Globe, Sparkles, Grid, Users, Smartphone, Download, GraduationCap, Music, Lock, Key
} from 'lucide-react';

export interface UserSession {
  name: string;
  email: string;
  role: string;
  tenant: string;
  subscriptionActive?: boolean;
  enabledModules?: string[];
}

export const AppShell: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'landing' | 'modules' | 'admin' | 'school_admin' | 'student_portal' | 'aac' | 'analytics' | 'downloads'>('landing');
  const [activeModuleId, setActiveModuleId] = useState('aac');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserSession | null>(null);
  const [isGlobalMusicPlaying, setIsGlobalMusicPlaying] = useState(false);

  const handleToggleGlobalMusic = () => {
    const newState = audioEngine.toggleGlobalBackgroundMusic();
    setIsGlobalMusicPlaying(newState);
  };

  const handleLoginSuccess = (user: UserSession) => {
    setCurrentUser(user);
    if (user.role === 'SUPER_ADMIN') {
      setActiveTab('admin');
    } else if (user.role === 'STUDENT') {
      setActiveTab('student_portal');
    } else {
      setActiveTab('school_admin');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setActiveTab('landing');
  };

  const handleLaunchModule = (moduleId: string) => {
    if (!currentUser) {
      setIsAuthModalOpen(true);
      return;
    }
    setActiveModuleId(moduleId);
    setActiveTab('modules');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans text-slate-900 selection:bg-blue-600 selection:text-white">
      
      {/* High-Contrast & Dyslexia Font Support Styles */}
      <style>{`
        .high-contrast {
          background-color: #000000 !important;
          color: #FFFF00 !important;
        }
        .high-contrast button, .high-contrast a, .high-contrast select, .high-contrast input {
          border: 2px solid #FFFF00 !important;
          background-color: #000000 !important;
          color: #FFFF00 !important;
        }
        .font-opendyslexic {
          font-family: 'Comic Sans MS', 'Arial', sans-serif !important;
          letter-spacing: 0.05em !important;
          word-spacing: 0.1em !important;
        }
      `}</style>

      <PWAInstallBanner />

      {/* Accessible Skip Link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-amber-400 text-slate-950 font-bold p-3 z-50 rounded-xl shadow-lg"
      >
        Skip to main content
      </a>

      {/* Main SaaS Platform Top Navigation Header */}
      <header className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white shadow-xl sticky top-0 z-40 border-b border-blue-500/20 px-4 py-3 flex items-center justify-between gap-4">
        
        {/* Brand Logo & Platform Identifier */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveTab('landing')}>
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center font-black text-white text-xl shadow-lg ring-2 ring-blue-400/30">
            A
          </div>
          <div>
            <div className="text-base font-black tracking-tight flex items-center gap-1.5">
              <span>AbilityOS</span>
              <span className="px-1.5 py-0.5 bg-amber-400 text-slate-950 text-[9px] font-extrabold rounded uppercase tracking-wider">
                ENTERPRISE SAAS
              </span>
            </div>
            <div className="text-[10px] text-blue-200 font-medium">Empowering Every Ability</div>
          </div>
        </div>

        {/* Center Main Tab Navigation (Desktop) */}
        <nav className="hidden lg:flex items-center gap-1 bg-white/10 p-1.5 rounded-2xl border border-white/10 backdrop-blur-md">
          
          <button
            onClick={() => setActiveTab('landing')}
            className={`px-3.5 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'landing' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            {t('saas_overview')}
          </button>

          {/* Publicly Visible Links */}
          <button
            onClick={() => setActiveTab('downloads')}
            className={`px-3.5 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'downloads' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
            {t('apps_downloads')}
          </button>

          <button
            onClick={() => setActiveTab('aac')}
            className={`px-3.5 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
              activeTab === 'aac' 
                ? 'bg-white text-blue-900 shadow' 
                : 'text-blue-100 hover:bg-white/10'
            }`}
          >
            <Volume2 className="w-3.5 h-3.5" />
            {t('aac_board')}
          </button>

          {/* POST-LOGIN ONLY TABS */}
          {currentUser && (
            <>
              <button
                onClick={() => setActiveTab('modules')}
                className={`px-3.5 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
                  activeTab === 'modules' 
                    ? 'bg-white text-blue-900 shadow' 
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                <Grid className="w-3.5 h-3.5 text-amber-400" />
                {t('module_suite')}
              </button>

              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-3.5 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all ${
                  activeTab === 'analytics' 
                    ? 'bg-white text-blue-900 shadow' 
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                {t('analytics')}
              </button>

              <button
                onClick={() => {
                  if (currentUser.role === 'SUPER_ADMIN') setActiveTab('admin');
                  else if (currentUser.role === 'STUDENT') setActiveTab('student_portal');
                  else setActiveTab('school_admin');
                }}
                className="px-3.5 py-1.5 rounded-xl font-extrabold text-xs bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 shadow-md flex items-center gap-1.5 transition-all ml-1"
              >
                <Shield className="w-3.5 h-3.5 text-slate-950" />
                <span>
                  {currentUser.role === 'SUPER_ADMIN' ? 'Super Admin HQ' : currentUser.role === 'STUDENT' ? 'Student Workspace' : 'School Admin Workspace'}
                </span>
              </button>
            </>
          )}

        </nav>

        {/* Language Switcher, Global Music Toggle & User Controls */}
        <div className="flex items-center gap-3">
          
          {/* Global Ambient Background Therapy Music Toggle */}
          <button
            onClick={handleToggleGlobalMusic}
            title={isGlobalMusicPlaying ? 'Mute Ambient Therapy Music' : 'Play Ambient Therapy Music'}
            className={`px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition border shadow-sm ${
              isGlobalMusicPlaying 
                ? 'bg-amber-400 text-slate-950 border-amber-300 animate-pulse' 
                : 'bg-blue-950/60 text-blue-200 border-white/10 hover:text-white'
            }`}
          >
            <Music className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{isGlobalMusicPlaying ? 'Music ON' : 'Music OFF'}</span>
          </button>

          {/* Language Switcher (EN, HI, MR) */}
          <div className="hidden sm:flex items-center bg-blue-950/60 p-1 rounded-xl border border-white/10 text-xs">
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`px-2.5 py-1 rounded font-bold transition ${i18n.language === 'en' ? 'bg-white text-blue-900 shadow-sm' : 'text-blue-200 hover:text-white'}`}
            >
              EN
            </button>
            <button
              onClick={() => i18n.changeLanguage('hi')}
              className={`px-2.5 py-1 rounded font-bold transition ${i18n.language === 'hi' ? 'bg-white text-blue-900 shadow-sm' : 'text-blue-200 hover:text-white'}`}
            >
              HI
            </button>
            <button
              onClick={() => i18n.changeLanguage('mr')}
              className={`px-2.5 py-1 rounded font-bold transition ${i18n.language === 'mr' ? 'bg-white text-blue-900 shadow-sm' : 'text-blue-200 hover:text-white'}`}
            >
              MR
            </button>
          </div>

          {/* Auth Button or User Profile Badge */}
          {currentUser ? (
            <div className="flex items-center gap-2 bg-white/10 pl-3 pr-1.5 py-1 rounded-xl border border-white/10 text-xs">
              <div className="text-right hidden sm:block">
                <div className="font-bold text-white leading-tight flex items-center gap-1">
                  <span>{currentUser.name}</span>
                  <span className="px-1.5 py-0.2 bg-amber-400 text-slate-950 font-mono text-[9px] rounded font-black uppercase">
                    {currentUser.role}
                  </span>
                </div>
                <div className="text-[10px] text-blue-200">{currentUser.tenant}</div>
              </div>
              <button
                onClick={handleLogout}
                title="Sign Out"
                className="p-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-200 border border-red-400/30 rounded-lg transition"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAuthModalOpen(true)}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl text-xs shadow-md transition flex items-center gap-2"
            >
              <LogIn className="w-4 h-4" />
              <span>{t('sign_in')}</span>
            </button>
          )}
        </div>

      </header>

      {/* Main Workspace Router with Clearance Padding */}
      <main id="main-content" className="flex-1 w-full pb-28 md:pb-16">
        {activeTab === 'landing' && (
          <SaaSLandingPage 
            onOpenAuth={() => setIsAuthModalOpen(true)}
            onExploreDemo={(tab) => {
              if (!currentUser) {
                setIsAuthModalOpen(true);
                return;
              }
              if (tab === 'admin' || tab === 'aac' || tab === 'analytics' || tab === 'downloads' || tab === 'school_admin' || tab === 'student_portal') {
                setActiveTab(tab as any);
              } else {
                handleLaunchModule(tab);
              }
            }}
            onSelectModule={handleLaunchModule}
          />
        )}
        
        {/* POST-LOGIN ONLY TAB ROUTING WITH AUTH GUARD */}
        {activeTab === 'modules' && (
          currentUser ? (
            <ModuleExplorer initialModule={activeModuleId} currentUser={currentUser} />
          ) : (
            <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-5">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner border border-blue-200">
                <Lock className="w-8 h-8" />
              </div>
              <div className="space-y-2">
                <span className="px-3 py-1 bg-amber-100 text-amber-900 text-xs font-black rounded-full uppercase tracking-wider border border-amber-300">
                  Authentication Required
                </span>
                <h3 className="text-2xl font-black text-slate-900">Sign In to Access 30-Module Suite</h3>
                <p className="text-xs text-slate-600 max-w-md mx-auto">
                  The interactive 30-module therapy & special education workspace is reserved for authorized organization members, therapists, administrators, and students.
                </p>
              </div>
              <button 
                onClick={() => setIsAuthModalOpen(true)} 
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-extrabold text-xs rounded-xl shadow-lg transition flex items-center gap-2 mx-auto"
              >
                <Key className="w-4 h-4 text-amber-300" />
                <span>Sign In / Demo Login</span>
              </button>
            </div>
          )
        )}

        {activeTab === 'downloads' && <DownloadAppsPage />}
        
        {activeTab === 'admin' && (
          currentUser?.role === 'SUPER_ADMIN' ? (
            <SuperAdminDashboard />
          ) : (
            <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-4">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner border border-purple-200">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black text-slate-900">Super Admin Authentication Required</h3>
              <p className="text-xs text-slate-600">Please sign in with a Super Admin account to access the system control plane.</p>
              <button onClick={() => setIsAuthModalOpen(true)} className="px-6 py-3 bg-purple-600 text-white font-extrabold text-xs rounded-xl shadow-lg">Sign In as Super Admin</button>
            </div>
          )
        )}

        {activeTab === 'school_admin' && (
          currentUser ? (
            <TenantAdminDashboard isSubscriptionActive={currentUser.subscriptionActive !== false} currentUser={currentUser} />
          ) : (
            <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner border border-blue-200">
                <Lock className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black text-slate-900">School Admin Sign In Required</h3>
              <p className="text-xs text-slate-600">Please sign in with your school administrator account to manage student rosters.</p>
              <button onClick={() => setIsAuthModalOpen(true)} className="px-6 py-3 bg-blue-600 text-white font-extrabold text-xs rounded-xl shadow-lg">Sign In</button>
            </div>
          )
        )}

        {activeTab === 'student_portal' && (
          currentUser ? (
            <StudentPortalDashboard currentUser={currentUser} onOpenAAC={() => setActiveTab('aac')} />
          ) : (
            <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-4">
              <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner border border-amber-200">
                <GraduationCap className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black text-slate-900">Student Portal Sign In Required</h3>
              <p className="text-xs text-slate-600">Please sign in with your student credentials or portal PIN to access your self-service portal.</p>
              <button onClick={() => setIsAuthModalOpen(true)} className="px-6 py-3 bg-amber-500 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg">Student Sign In</button>
            </div>
          )
        )}

        {activeTab === 'aac' && <AACBoard />}

        {activeTab === 'analytics' && (
          currentUser ? (
            <ExecutiveDashboard />
          ) : (
            <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner border border-emerald-200">
                <TrendingUp className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black text-slate-900">Analytics BI Sign In Required</h3>
              <p className="text-xs text-slate-600">Please sign in to view executive progress analytics and 30-module clinical mastery telemetry.</p>
              <button onClick={() => setIsAuthModalOpen(true)} className="px-6 py-3 bg-emerald-600 text-white font-extrabold text-xs rounded-xl shadow-lg">Sign In</button>
            </div>
          )
        )}
      </main>

      {/* Mobile Smartphone Bottom Tab Navigation Bar */}
      <MobileAppNavigation 
        activeTab={activeTab}
        onTabChange={(tab) => {
          if (!currentUser && (tab === 'modules' || tab === 'school_admin' || tab === 'analytics' || tab === 'student_portal')) {
            setIsAuthModalOpen(true);
            return;
          }
          setActiveTab(tab as any);
        }}
        currentUser={currentUser}
      />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Floating Accessibility Toolbar */}
      <AccessibilityToolbar />
    </div>
  );
};
